import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { useParams } from 'react-router';
import { DetailsCard } from './components/details-card';
import { StatusDialog } from './components/status-dialog';
import { checkoutSteps } from './constants/checkout-steps';
import {
  type CheckoutFlowValues,
  useCheckoutWizard,
} from './hooks/useCheckoutWizard';
import { usePayment } from './hooks/usePayment';
import { schemasByStep } from './schema';
import { CheckoutStepIds } from './types';
import { getNextStepId } from './utils/steps-navigation';
import { PageContainer } from '@/components/shared/page-container';
import { PageSkeleton } from '@/components/shared/page-skeleton';
import { EnrollmentEvent } from '@/constants/analytics';
import { Wizard } from '@/context/wizard/components/wizard';
import { WizardProvider } from '@/context/wizard/context';
import { useCatalogOffer } from '@/data/catalog/offer';
import { useCheckOrder } from '@/data/orders';
import type { RegistrationFormData } from '@/packages/courses/components/user-data-form/schema';
import { buildOfferEnrollmentProperties } from '@/packages/courses/utils/enrollment-analytics';
import { track } from '@/utils/analytics';

const CheckoutContent = () => {
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const {
    currentStepId,
    actions,
    values,
    offer,
    paymentMethod,
    customerInfo,
    orderId,
  } = useCheckoutWizard();
  const { status: orderStatus, isSuccess, isFailed } = useCheckOrder(orderId!);
  const { handlePaymentProcess, handleRegistration, isLoading } = usePayment();

  const currentSchema = schemasByStep[currentStepId];
  const stepValues = values[currentStepId];
  const defaultValues =
    stepValues ??
    (currentStepId === CheckoutStepIds.CustomerInfo
      ? { countryCode: '+55' }
      : {});

  const methods = useForm<Record<string, unknown>>({
    resolver: zodResolver(currentSchema) as unknown as Resolver<
      Record<string, unknown>
    >,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: defaultValues as Record<string, unknown>,
  });

  const isEditing = !!customerInfo && !!paymentMethod;
  const shouldShowDetailsCard =
    currentStepId !== CheckoutStepIds.PaymentStatus &&
    currentStepId !== CheckoutStepIds.PixBankSlip;

  const handleStepNavigation = (values: CheckoutFlowValues) => {
    const nextStepId = getNextStepId(currentStepId, values);
    const navigateToConfirmation =
      isEditing && currentStepId === CheckoutStepIds.CustomerInfo;

    if (navigateToConfirmation) {
      actions.navigate(CheckoutStepIds.DataConfirmation);
      return;
    }

    if (nextStepId === CheckoutStepIds.PaymentStatus) return;

    if (nextStepId) actions.navigate(nextStepId);
  };

  const handleContinue = async (data: Record<string, unknown>) => {
    actions.updateValues({ [currentStepId]: data });

    const updatedValues = { ...values, [currentStepId]: data };
    const shouldCreateRegistration =
      currentStepId === CheckoutStepIds.CustomerInfo && !isEditing && offer?.id;

    if (shouldCreateRegistration) {
      handleRegistration(data as RegistrationFormData);
      return;
    }

    if (currentStepId === CheckoutStepIds.DataConfirmation) {
      handlePaymentProcess();
      return;
    }

    handleStepNavigation(updatedValues);
  };

  useEffect(() => {
    track(EnrollmentEvent.CheckoutStepViewed, {
      ...buildOfferEnrollmentProperties(offer),
      step: currentStepId,
      payment_method: paymentMethod,
    });
    // Only a step change counts as a view; applying a coupon also changes the offer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  useEffect(() => {
    if (!isSuccess && !isFailed) return;

    const properties = {
      ...buildOfferEnrollmentProperties(offer),
      payment_method: paymentMethod,
    };

    if (isSuccess) {
      track(EnrollmentEvent.CheckoutPaymentConfirmed, {
        ...properties,
        amount: offer?.checkout_pricing?.calculated_amount,
      });
    } else {
      track(EnrollmentEvent.CheckoutPaymentFailed, {
        ...properties,
        stage: 'payment_not_identified',
        error_reason: orderStatus,
      });
    }

    setOpenStatusDialog(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isFailed]);

  useEffect(() => {
    const stepValues = values[currentStepId];
    const nextDefaults =
      stepValues ??
      (currentStepId === CheckoutStepIds.CustomerInfo
        ? { countryCode: '+55' }
        : {});
    methods.reset(nextDefaults as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId]);

  return (
    <FormProvider {...methods}>
      <Stack direction={{ md: 'row' }} gap={4} alignItems="flex-start">
        <Stack
          width={!shouldShowDetailsCard ? { md: '561px' } : '100%'}
          margin={shouldShowDetailsCard ? undefined : '0 auto'}
          justifyContent={shouldShowDetailsCard ? undefined : 'center'}
          alignItems={shouldShowDetailsCard ? undefined : 'center'}
        >
          <Wizard steps={checkoutSteps} />
        </Stack>
        {shouldShowDetailsCard && (
          <DetailsCard
            offer={offer!}
            onContinue={methods.handleSubmit(handleContinue)}
            isLoading={isLoading}
          />
        )}
      </Stack>
      {openStatusDialog && (
        <StatusDialog
          status={isSuccess ? 'success' : 'failed'}
          onClose={() => setOpenStatusDialog(false)}
        />
      )}
    </FormProvider>
  );
};

export const Checkout = () => {
  const { offerId } = useParams<{ id: string; offerId: string }>();
  const { offer, isLoading } = useCatalogOffer(Number(offerId));

  const initialState = {
    currentStepId: CheckoutStepIds.CustomerInfo,
    values: { offer },
  };

  if (isLoading || !offer) return <PageSkeleton />;

  return (
    <PageContainer>
      <WizardProvider initialState={initialState}>
        <CheckoutContent />
      </WizardProvider>
    </PageContainer>
  );
};
